import { applySnapshot, flow, getSnapshot, types } from 'mobx-state-tree';
import isEqual from 'lodash/isEqual';
import { getConstantName } from 'utils';
import { CONNECTION_STATUS_TEXT } from '../constants';
import { Crystal } from 'http-requests';
import { RequestStateModel } from '../../general-models';
import { AddressMappingsModel } from './address-mappings';
import { SignalMappingsModel } from './signal-mappings';

export const CrystalConnectionStatusMST = types.enumeration(
  'CrystalConnectionStatus',
  ['connected', 'failure', 'new']
);

const getMainInfoSnapshot = (self: {
  kristall_id: string;
  ip_address: string;
  port: number;
  name: string;
  address: string;
  connection_status: 'connected' | 'failure' | 'new';
}) => ({
  kristall_id: self.kristall_id,
  ip_address: self.ip_address,
  port: self.port,
  name: self.name,
  address: self.address,
  connection_status: self.connection_status,
});

const StatusesModel = types.model('CrystalStatuses', {
  update: types.optional(RequestStateModel, {}),
  loadMappings: types.optional(RequestStateModel, {}),
});

export const CrystalModel = types
  .model('CrystalModel', {
    statuses: types.optional(StatusesModel, {}),
    kristall_id: types.string,
    ip_address: types.string,
    port: types.number,
    name: types.string,
    address: types.string,
    connection_status: CrystalConnectionStatusMST,
    addressMappings: types.optional(AddressMappingsModel, {}),
    signalMappings: types.optional(SignalMappingsModel, {}),
  })
  .volatile((self) => ({
    isEdit: false as boolean,
    editedModelValue: getMainInfoSnapshot(self),
  }))
  .views((self) => ({
    get isModified(): boolean {
      return (
        !isEqual(getMainInfoSnapshot(self), self.editedModelValue) ||
        self.addressMappings.isModified ||
        self.signalMappings.isModified
      );
    },
    get data() {
      return self.isEdit ? self.editedModelValue : getMainInfoSnapshot(self);
    },
    get listSyncSnapshot() {
      const currentData = self.isEdit
        ? self.editedModelValue
        : getMainInfoSnapshot(self);

      return {
        ...getSnapshot(self),
        ...currentData,
        addressMappings: {
          ...getSnapshot(self.addressMappings),
          isEdit: false,
          mappings: getSnapshot(self.addressMappings.data),
          editedMappings: getSnapshot(self.addressMappings.data),
        },
        signalMappings: {
          ...getSnapshot(self.signalMappings),
          isEdit: false,
          mappings: getSnapshot(self.signalMappings.data),
          editedMappings: getSnapshot(self.signalMappings.data),
        },
      };
    },
    get isMappingsLoaded() {
      return self.statuses.loadMappings.fulfilled;
    },
    get formattedMainInfo() {
      return `Кристалл ID: ${self.kristall_id}
      
Статус подключения: ${getConstantName(
        CONNECTION_STATUS_TEXT,
        self.connection_status
      )}
            
Адрес: ${self.address}

Название пульта: ${self.name}
      
IP адрес: ${self.ip_address}
      
Порт: ${self.port}`;
    },
  }))
  .actions((self) => ({
    /**
     * Поля изменяются во временном хранилище, замена в основной модели
     * должна происходить при сохранении.
     */
    changeField<
      K extends keyof Omit<typeof self.editedModelValue, 'kristall_id'>
    >(field: K, value: typeof self.editedModelValue[K]) {
      self.editedModelValue[field] = value;
    },
    loadMappings: flow(function* (force = false) {
      if (
        self.statuses.loadMappings.pending ||
        (!force && self.isMappingsLoaded)
      ) {
        return;
      }

      try {
        self.statuses.loadMappings.loadingStates();

        const [directoryResponse, houseMappings, signalMappings]: [
          Crystal.DirectoryDTO,
          Crystal.HouseMappingDTO,
          Crystal.EventMappingDTO
        ] = yield Promise.all([
          Crystal.GetDirectory(self.kristall_id),
          Crystal.GetHouseCategoryMappings(self.kristall_id),
          Crystal.GetEventCategoryMappings(self.kristall_id),
        ]);

        self.addressMappings.hydrate(directoryResponse.houses, houseMappings);
        self.signalMappings.hydrate(directoryResponse.events, signalMappings);

        self.statuses.loadMappings.successStates();
      } catch (error) {
        console.error(error);
        self.statuses.loadMappings.errorStates();

        return Promise.reject(error);
      } finally {
        self.statuses.loadMappings.finallyStates();
      }
    }),
    /**
     * Отмена редактирования. Не забыть добавить экшн на анмаунт страницы кристалла.
     */
    cancelEdit() {
      self.isEdit = false;
      self.editedModelValue = getMainInfoSnapshot(self);
      self.addressMappings.cancelEdit();
      self.signalMappings.cancelEdit();
    },
    startEdit() {
      self.isEdit = true;
      self.editedModelValue = getMainInfoSnapshot(self);
      self.addressMappings.startEdit();
      self.signalMappings.startEdit();
    },
    /**
     * Сохранение изменений в основной модели.
     * Возвращается snapshot основной модели.
     */
    saveEdit: flow(function* () {
      if (!self.isModified) {
        return;
      }

      try {
        self.statuses.update.loadingStates();

        if (!isEqual(getMainInfoSnapshot(self), self.editedModelValue)) {
          const { kristall_id, ...rest } = self.editedModelValue;

          yield Crystal.UpdateCrystal(kristall_id, rest);
          applySnapshot(self, {
            ...getSnapshot(self),
            ...self.editedModelValue,
          });
        }

        yield Promise.all([
          self.addressMappings.save(self.kristall_id),
          self.signalMappings.save(self.kristall_id),
        ]);

        self.statuses.update.successStates();
        self.isEdit = false;
        self.editedModelValue = getMainInfoSnapshot(self);
        self.addressMappings.isEdit = false;
        self.signalMappings.isEdit = false;

        return getSnapshot(self);
      } catch (error) {
        console.error(error);
        self.statuses.update.errorStates();

        return Promise.reject(error);
      } finally {
        self.statuses.update.finallyStates();
      }
    }),
  }));
